import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../api/client';
import { useUIStore } from '../../../stores/uiStore';
import {
  Rocket, Search, FileText, Eye, CheckCircle2, XCircle,
  Clock, AlertCircle, Loader2, Package, Trash2, RefreshCw,
  ChevronLeft, ChevronRight, X, Send, Sparkles, Link,
  PenTool, Grid3X3, BarChart3, Filter, Check, Ban,
  TrendingUp, Binoculars, FolderTree, Plus,
  Play, Pause, Settings2, ChevronDown, ChevronUp,
  Globe, Star, Weight, ShoppingCart, Zap, Edit3, Layers
} from 'lucide-react';
import { ManualPublishForm } from './ManualPublishForm';

function FromLinkForm() {
  const { t } = useTranslation();
  const { sellerId } = useUIStore();
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [extractedData, setExtractedData] = useState<any>(null);
  const [error, setError] = useState('');

  const handleExtract = async () => {
    if (!url.trim()) return;
    setLoading(true);
    setError('');
    setExtractedData(null);
    try {
      const res = await api.post<any>('/publisher/manual/from-link', { url: url.trim(), seller_id: sellerId });
      if (res.success && res.data) {
        if (res.data.source === 'not_found') {
          setError(t('pub.manual.onlyAmazon'));
        } else {
          setExtractedData(res.data);
        }
      } else {
        setError(res.error || t('pub.manual.onlyAmazon'));
      }
    } catch (err) {
      setError('Error extracting data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* URL Input */}
      <div className="bg-[var(--color-bg-secondary)] rounded-lg border border-[var(--color-border)] p-4">
        <label className="text-sm font-medium text-[var(--color-text)] block mb-2">{t('pub.manual.pasteUrl')}:</label>
        <div className="flex gap-2">
          <input
            type="text"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleExtract()}
            placeholder="https://www.amazon.com/dp/B08XYZ..."
            className="flex-1 px-3 py-2 bg-[var(--color-bg)] border border-[var(--color-border)] rounded-lg text-sm text-[var(--color-text)]"
          />
          <button
            onClick={handleExtract}
            disabled={loading || !url.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {t('pub.manual.go')}
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 mt-3 text-sm text-purple-600">
            <Loader2 size={14} className="animate-spin" />
            {t('pub.manual.extracting')}
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 mt-3 text-sm text-red-500">
            <AlertCircle size={14} />
            {error}
          </div>
        )}

        {extractedData && !error && (
          <div className="mt-3 space-y-1">
            <div className="flex items-center gap-2 text-sm text-green-600">
              <CheckCircle2 size={14} />
              {t('pub.manual.extracted')}: {extractedData.title?.substring(0, 60) || extractedData.asin}
            </div>
            {extractedData.images?.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 size={14} />
                {extractedData.images.length} {t('pub.manual.imagesFound')}
              </div>
            )}
            {extractedData.category_tree && (
              <div className="flex items-center gap-2 text-sm text-green-600">
                <CheckCircle2 size={14} />
                {t('pub.manual.categorySuggested')}: {extractedData.category_tree}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Pre-filled form */}
      {extractedData && (
        <div>
          <h3 className="text-sm font-medium text-[var(--color-text)] mb-2 flex items-center gap-2">
            <Edit3 size={14} />
            {t('pub.manual.editableResult')}
          </h3>
          <ManualPublishForm prefillData={extractedData} />
        </div>
      )}
    </div>
  );
}

export { FromLinkForm };
